// What a supplier invoice line says about its quantity against the delivery's
// BDN (bunker delivery note). Ledger 166, Chris's calls 2026-10-11:
//   * the line is filled with the signed BDN tonnage;
//   * before the BDN is in, the contract quantity, marked "BDN not in yet";
//   * a person may type the supplier's own figure, and the line then says how
//     far it is from the BDN.
//
// The server sends the figures (``bdn_check`` on the line: basis,
// bdn_quantity, hms_quantity (what the system filled in), unit, bdn_number,
// reason -- backend/invoicing/services/delivery_bills.bdn_check). The words
// live here, once, for the supplier invoice screen, the approval box and the
// delivery's Bills to receive list, because the gap moves as a person types.
import { formatFigure, subtractFigures, QUANTITY_DECIMALS } from './precision';

const shown = (value) => formatFigure(value, 2, QUANTITY_DECIMALS);

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

// Same figure, whatever trailing zeros either side carries ('150' vs '150.0000').
const sameFigure = (a, b) => !isBlank(a) && !isBlank(b) && subtractFigures(a, b, QUANTITY_DECIMALS) === '0';

// null when the line reads no BDN at all (a cost, a purchase order, an older
// invoice). Otherwise { tone: 'ok' | 'warn', text }. The unit is the line's own
// as the server sends it (a lubes line counts drums); ``unit`` is only the
// fallback for a line that does not carry one.
export const bdnQuantityNote = (quantity, bdnCheck, unit = 'MT') => {
  if (!bdnCheck || !bdnCheck.basis) return null;
  const lineUnit = bdnCheck.unit || unit;
  const unitText = lineUnit ? ` ${lineUnit}` : '';

  if (bdnCheck.basis === 'bdn') {
    const bdn = bdnCheck.bdn_quantity;
    const which = bdnCheck.bdn_number ? ` ${bdnCheck.bdn_number}` : '';
    if (isBlank(quantity)) {
      return { tone: 'warn', text: `BDN${which} says ${shown(bdn)}${unitText}` };
    }
    if (sameFigure(quantity, bdn)) {
      return { tone: 'ok', text: `From BDN${which}` };
    }
    const gap = subtractFigures(quantity, bdn, QUANTITY_DECIMALS);
    const more = !gap.startsWith('-');
    return {
      tone: 'warn',
      text: `${shown(more ? gap : gap.slice(1))}${unitText} ${more ? 'more' : 'less'} than BDN${which} (${shown(bdn)}${unitText})`,
    };
  }

  const isContract = sameFigure(quantity, bdnCheck.hms_quantity);
  const lead = bdnCheck.basis === 'no_bdn'
    ? 'BDN not in yet'
    : `Not matched to a BDN line${bdnCheck.reason ? ` (${bdnCheck.reason})` : ''}`;
  return { tone: 'warn', text: isContract ? `${lead}: contract quantity` : lead };
};

// The lines an approver should look at before approving: every bought-fuel
// line whose quantity is not the BDN's. Plain sentences, "Line 1: ...".
export const bdnApprovalWarnings = (lineItems = []) => (
  lineItems
    .map((line, index) => {
      const note = bdnQuantityNote(line.quantity, line.bdn_check, line.unit_code || 'MT');
      return note && note.tone === 'warn' ? `Line ${line.line_number || index + 1}: ${note.text}` : null;
    })
    .filter(Boolean)
);
